import { Icon } from "@/components";
import { toDay, parseTime } from "@/lib/datetime";
import { useI18n } from "@/lib/i18n/i18n_provider";
import {
  formatMeetingRoomDateRange,
  formatDayRange,
} from "@/offering/format_date_range";
import { MeetingRoomDateRange } from "@/offering/order_params";
import { Spacer } from "@/offering/spacer";
import { TimePicker } from "@/offering/time_picker";
import { addHours } from "@/offering/time_utils";
import { getAvailableDays } from "@/offering_v2/utils/get_available_days";
import classNames from "classnames";
import { startOfMonth, endOfMonth, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { ListGroup, Dropdown, Button, Modal } from "react-bootstrap";
import { colors } from "../colors";
import {
  DateTimeValue,
  isHourlyValue,
} from "../date-time-picker/date_time_picker";
import {
  DateRangePicker,
  PickingRange,
} from "../date_range_picker/date_range_picker";
import { DateTimeFilterDropdown } from "./date_time_filter_dropdown";
import styles from "./gallery_mobile_filters.module.css";
import { useDateTimeDFilterFullscreenFeatureFlag } from "@/core/splitio";
import { ScrollableDateTimePicker } from "../date-time-picker/scrollable_date_time_picker";

interface MobileDateTimeFilterItemV2Props {
  onChange: (value: MeetingRoomDateRange) => void;
  value: MeetingRoomDateRange;
  label: string;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
  disabled?: boolean;
}

export function MobileDateTimeFilterItemV2({
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
  disabled,
}: MobileDateTimeFilterItemV2Props) {
  const { fbt, locale } = useI18n();
  const [localValue, setLocalValue] = useState<DateTimeValue | undefined>(
    value
  );
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (value) {
      setLocalValue(value);
    }
  }, [value]);

  const displayValue = isHourlyValue(localValue)
    ? formatMeetingRoomDateRange(localValue, fbt, locale, "")
    : localValue?.startDate &&
      localValue.endDate &&
      formatDayRange(
        {
          start: localValue.startDate,
          end: localValue.endDate,
        },
        fbt,
        locale
      );

  const handleSeeResults = () => {
    setShowCalendar(false);
    const defaultValue = {
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      startDate: undefined,
      endDate: undefined,
    };
    onChange({ ...defaultValue, ...localValue });
  };

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown
        show={showCalendar}
        className={classNames("checkout-box-picker", styles.dropdown)}
      >
        <Dropdown.Toggle
          disabled={disabled}
          onClick={() => {
            setShowCalendar((v) => !v);
            showCalendar && handleSeeResults();
          }}
        >
          {displayValue ? (
            <div className={styles.displayValue}>{displayValue}</div>
          ) : (
            <div className={styles.placeholder}>{label}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu align="end">
          <div>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              <DateTimeFilterDropdown
                value={localValue}
                onChange={setLocalValue}
                openAt={openAt}
                closedAt={closedAt}
                timeSlotIncrement={timeSlotIncrement}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div
                role="button"
                style={{
                  color: displayValue
                    ? "var(--bs-primary)"
                    : colors.blackMinus60,
                }}
                onClick={() => setLocalValue(undefined)}
              >
                {fbt("Clear selection")}
              </div>
              <Button
                disabled={!displayValue}
                onClick={handleSeeResults}
                style={{ width: "fit-content" }}
              >
                {fbt("See results")}
              </Button>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface MobileModalDateTimeFilterItemV2Props {
  onChange: (value: DateTimeValue) => void;
  value?: DateTimeValue;
  label: string;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
  disabled?: boolean;
}

export function MobileModalDateTimeFilterItemV2({
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
  disabled,
}: MobileModalDateTimeFilterItemV2Props) {
  const { fbt, locale } = useI18n();
  const dateTimeDFilterFullscreenFeatureFlag =
    useDateTimeDFilterFullscreenFeatureFlag();

  const [localValue, setLocalValue] = useState<DateTimeValue | undefined>(
    value
  );
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  const displayValue = isHourlyValue(value)
    ? formatMeetingRoomDateRange(value, fbt, locale, "")
    : value?.startDate &&
      value.endDate &&
      formatDayRange(
        {
          start: value.startDate,
          end: value.endDate,
        },
        fbt,
        locale
      );

  const handleSave = () => {
    setShowCalendarDropdown(false);
    setShowCalendarModal(false);
    const defaultValue = {
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      startDate: undefined,
      endDate: undefined,
    };
    onChange({ ...defaultValue, ...localValue });
  };

  return dateTimeDFilterFullscreenFeatureFlag ? (
    <>
      <ListGroup.Item
        className="d-flex justify-content-between align-items-center"
        onClick={() => setShowCalendarModal(true)}
        disabled={disabled}
      >
        <div className={styles.displayValue}>
          <div className={styles.label}>{label}</div>
          <Spacer size={8} />
          {displayValue ? (
            <div>{displayValue}</div>
          ) : (
            <div className={styles.placeholder}>
              {fbt(
                disabled ? "Select a location to filter by date" : "Any time"
              )}
            </div>
          )}
        </div>
        <div>
          <Icon name="fi-chevron-down" color="#82808E" />
        </div>
      </ListGroup.Item>

      <Modal
        show={showCalendarModal}
        fullscreen
        onHide={() => {
          setShowCalendarModal(false);
        }}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <b>{fbt("When")}</b>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ScrollableDateTimePicker
            value={localValue}
            onChange={setLocalValue}
            openAt={openAt}
            closedAt={closedAt}
            timeSlotIncrement={timeSlotIncrement}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div
              role="button"
              style={{
                color: displayValue ? "var(--bs-primary)" : colors.blackMinus60,
              }}
              onClick={() => setLocalValue(undefined)}
            >
              {fbt("Clear selection")}
            </div>
            <Button onClick={handleSave} style={{ width: "fit-content" }}>
              {fbt("Save")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  ) : (
    <ListGroup.Item className={`${styles.filterItem} mobile-filter-item`}>
      <Dropdown
        show={showCalendarDropdown}
        className={classNames("checkout-box-picker", styles.modalDropdown)}
      >
        <Dropdown.Toggle
          style={{ padding: 0 }}
          disabled={disabled}
          onClick={() => setShowCalendarDropdown((v) => !v)}
        >
          <div className={styles.displayValue}>
            <div className={styles.label}>{label}</div>
            <Spacer size={8} />
            {displayValue ? (
              <div>{displayValue}</div>
            ) : (
              <div className={styles.placeholder}>
                {fbt(
                  disabled ? "Select a location to filter by date" : "Any time"
                )}
              </div>
            )}
          </div>
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <div>
            <DateTimeFilterDropdown
              value={localValue}
              onChange={setLocalValue}
              openAt={openAt}
              closedAt={closedAt}
              timeSlotIncrement={timeSlotIncrement}
            />
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div
                role="button"
                style={{
                  color: displayValue
                    ? "var(--bs-primary)"
                    : colors.blackMinus60,
                }}
                onClick={() => setLocalValue(undefined)}
              >
                {fbt("Clear selection")}
              </div>
              <Button onClick={handleSave} style={{ width: "fit-content" }}>
                {fbt("Save")}
              </Button>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </ListGroup.Item>
  );
}

export function MobileDateTimeFilterItem({
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
}: MobileModalDateTimeFilterItemProps) {
  const { fbt, locale } = useI18n();

  const today = new Date();
  const [viewingRange, setViewRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({ startDate: startOfMonth(today), endDate: endOfMonth(today) });

  const displayValue = formatMeetingRoomDateRange(value, fbt, locale, "");
  const availableDates = getAvailableDays(viewingRange);

  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown className={classNames("checkout-box-picker", styles.dropdown)}>
        <Dropdown.Toggle>
          {displayValue ? (
            <div className={styles.displayValue}>{displayValue}</div>
          ) : (
            <div className={styles.placeholder}>{label}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {displayValue && (
          <div
            className={styles.clearBtn}
            onClick={() =>
              onChange({
                date: undefined,
                startTime: undefined,
                endTime: undefined,
              })
            }
          >
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu align="end">
          <div>
            <DateRangePicker
              availableDates={availableDates}
              pickingRange={PickingRange.OneDate}
              value={{
                startDate: value.date ? parseISO(value.date) : undefined,
              }}
              onChange={(value) =>
                onChange({
                  date: toDay(value.startDate),
                })
              }
              onViewDates={(startDate, endDate) =>
                setViewRange({ startDate, endDate })
              }
            />
            <div className="px-3 pb-3 d-flex">
              <TimePicker
                value={value.startTime}
                placeholder={fbt("Start time")}
                startTime={openAt}
                endTime={closedAt}
                step={timeSlotIncrement}
                onChange={(startTime) => onChange({ ...value, startTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.date}
              />
              <Spacer size={30} direction="horizontal" />
              <TimePicker
                value={value.endTime}
                step={timeSlotIncrement}
                placeholder={fbt("End time")}
                startTime={addHours(value.startTime || openAt || "0800", 1)}
                endTime={closedAt}
                onChange={(endTime) => onChange({ ...value, endTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.startTime}
              />
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface MobileModalDateTimeFilterItemProps {
  onChange: (value: MeetingRoomDateRange) => void;
  value: MeetingRoomDateRange;
  label: string;
  openAt?: string;
  closedAt?: string;
  timeSlotIncrement?: number;
}

export function MobileModalDateTimeFilterItem({
  onChange,
  value,
  label,
  openAt,
  closedAt,
  timeSlotIncrement,
}: MobileModalDateTimeFilterItemProps) {
  const { fbt, locale } = useI18n();
  const today = new Date();
  const [viewingRange, setViewRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({ startDate: startOfMonth(today), endDate: endOfMonth(today) });

  const displayValue = formatMeetingRoomDateRange(value, fbt, locale, "");
  const availableDates = getAvailableDays(viewingRange);

  return (
    <ListGroup.Item className={`${styles.filterItem} mobile-filter-item`}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.modalDropdown)}
      >
        <Dropdown.Toggle style={{ padding: 0 }}>
          <div className={styles.displayValue}>
            <div className={styles.label}>{label}</div>
            <Spacer size={8} />
            {displayValue ? (
              <div>{displayValue}</div>
            ) : (
              <div className={styles.placeholder}>{fbt("Any")}</div>
            )}
          </div>
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {displayValue && (
          <div
            className={styles.clearBtn}
            onClick={() => {
              onChange({
                date: undefined,
                startTime: undefined,
                endTime: undefined,
              });
            }}
          >
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu>
          <div>
            <DateRangePicker
              availableDates={availableDates}
              pickingRange={PickingRange.OneDate}
              value={{
                startDate: value.date ? parseISO(value.date) : undefined,
              }}
              onChange={(value) =>
                onChange({
                  date: toDay(value.startDate),
                })
              }
              onViewDates={(startDate, endDate) =>
                setViewRange({ startDate, endDate })
              }
            />
            <div className="px-3 pb-3 d-flex">
              <TimePicker
                value={value.startTime}
                placeholder={fbt("Start time")}
                startTime={openAt}
                endTime={closedAt}
                step={timeSlotIncrement}
                onChange={(startTime) => onChange({ ...value, startTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.date}
              />
              <Spacer size={30} direction="horizontal" />
              <TimePicker
                value={value.endTime}
                step={timeSlotIncrement}
                placeholder={fbt("End time")}
                startTime={addHours(value.startTime || openAt || "0800", 1)}
                endTime={closedAt}
                onChange={(endTime) => onChange({ ...value, endTime })}
                formatter={(time) => fbt.formatTime(parseTime(time))}
                disabled={!value.startTime}
              />
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </ListGroup.Item>
  );
}
